import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment';
import {
  CustomerCreateOrUpdateRequest,
  CustomerDetail,
  CustomerListParams,
  CustomerListResponse,
  CustomerReservationSummary,
  CustomerSearchItem,
} from '@models/customers';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class CustomersService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiUrl;

  getCustomers(params: CustomerListParams): Observable<CustomerListResponse> {
    let httpParams = new HttpParams().set('page', params.page).set('pageSize', params.pageSize);

    if (params.search) httpParams = httpParams.set('search', params.search);
    if (params.id != null) httpParams = httpParams.set('id', params.id);
    if (params.birthDateFrom) httpParams = httpParams.set('birthDateFrom', params.birthDateFrom);
    if (params.birthDateTo) httpParams = httpParams.set('birthDateTo', params.birthDateTo);
    if (params.createdFrom) httpParams = httpParams.set('createdFrom', params.createdFrom);
    if (params.createdTo) httpParams = httpParams.set('createdTo', params.createdTo);
    if (params.firstName) httpParams = httpParams.set('firstName', params.firstName);
    if (params.lastName) httpParams = httpParams.set('lastName', params.lastName);
    if (params.email) httpParams = httpParams.set('email', params.email);
    if (params.address) httpParams = httpParams.set('address', params.address);
    if (params.postalCode) httpParams = httpParams.set('postalCode', params.postalCode);
    if (params.city) httpParams = httpParams.set('city', params.city);
    if (params.phoneNumber) httpParams = httpParams.set('phoneNumber', params.phoneNumber);
    if (params.siteId != null) httpParams = httpParams.set('siteId', params.siteId);
    if (params.genderId != null) httpParams = httpParams.set('genderId', params.genderId);
    if (params.statusId != null) httpParams = httpParams.set('statusId', params.statusId);
    if (params.countryId != null) httpParams = httpParams.set('countryId', params.countryId);
    if (params.locale) httpParams = httpParams.set('locale', params.locale);
    if (params.hasAppAccount != null) httpParams = httpParams.set('hasAppAccount', params.hasAppAccount);

    return this.http.get<CustomerListResponse>(`${this.baseUrl}/api/customers`, {
      params: httpParams,
    });
  }

  /**
   * Name search for autocomplete dropdowns (booker / co-traveler pickers). Backed by the dedicated
   * /api/customers/search endpoint: full-text match over name + primary email, active + non-dummy
   * customers, ordered by name.
   */
  searchCustomers(query: string, limit = 10): Observable<CustomerSearchItem[]> {
    const params = new HttpParams().set('q', query).set('limit', limit);
    return this.http.get<CustomerSearchItem[]>(`${this.baseUrl}/api/customers/search`, { params });
  }

  deleteCustomer(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/api/customers/${id}`);
  }

  getCustomer(id: number): Observable<CustomerDetail> {
    return this.http.get<CustomerDetail>(`${this.baseUrl}/api/customers/${id}`);
  }

  createCustomer(data: CustomerCreateOrUpdateRequest): Observable<CustomerDetail> {
    return this.http.post<CustomerDetail>(`${this.baseUrl}/api/customers`, data);
  }

  updateCustomer(id: number, data: CustomerCreateOrUpdateRequest): Observable<CustomerDetail> {
    return this.http.put<CustomerDetail>(`${this.baseUrl}/api/customers/${id}`, data);
  }

  getCustomerReservations(id: number): Observable<CustomerReservationSummary[]> {
    return this.http.get<CustomerReservationSummary[]>(`${this.baseUrl}/api/customers/${id}/reservations`);
  }
}
